// Порт ref DesignBlockEditor.tsx:617-703 (case 'badge'): пилюли по центру с переносом,
// размер авто по количеству. В правке текст и иконка правятся прямо в пилюле
// (`EditableBadgePill`), как и в панели справа.
// Отличие от рефа: у нас нет легаси-полей `badgeText`/`iconType: 'emoji'` — пустой
// список показываем одной плейсхолдер-пилюлей, эмодзи-ветку не портируем.
import { cn } from '@teach/ui'
import { useStrictLang } from './strictLangContext'
import type { DesignBadge, DesignBadgeItem } from '@/types/blocks'
import { badgeItems, newBadgeItem } from '@/features/editor/lib/designElements'
import { httpsMediaSrc } from '@/features/editor/lib/designUrl'
import { getLucideIcon } from '@/features/editor/lib/iconCatalog'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import { resolveBadgeVariant } from '@/features/editor/lib/badgeVariants'
import type { DesignElementProps } from './DesignElementView'
import { EditableBadgePill } from './EditableBadgePill'
import {
  BADGE_ROW_CLASS,
  badgeIconSizeClass,
  badgeSizeClass,
  badgeSizeOf,
  badgeVariantStyle,
  type AutoBadgeSize,
} from '@/features/editor/lib/badgeStyles'

// Поля заглушки берём из той же фабрики, что форма и дефолт элемента, чтобы они не
// разошлись с настоящим бейджем. Но НЕ на уровне модуля: фабрика зовёт
// `crypto.randomUUID()`, который есть только в secure context, и его падение при
// инициализации модуля унесло бы весь чанк редактора, а не одну заглушку.
// id статический — он тут только ключ списка, случайность ему не нужна.
const placeholderBadge = (): DesignBadgeItem => ({ ...newBadgeItem('Новый'), id: 'placeholder' })

const BadgeIcon = ({ badge, size }: { badge: DesignBadgeItem; size: AutoBadgeSize }) => {
  const className = badgeIconSizeClass(size)
  if (badge.iconType === 'none' || !badge.iconValue) return null
  if (badge.iconType === 'lucide') {
    const LucideIcon = getLucideIcon(badge.iconValue)
    return LucideIcon ? <LucideIcon className={className} /> : null
  }
  // Кастомная иконка — произвольный URL автора: политика ссылок в designUrl.
  const src = httpsMediaSrc(badge.iconValue)
  return src ? <img src={src} alt="" className={cn(className, 'object-contain')} /> : null
}

export const BadgeElement = ({
  element,
  editable,
  contentLang,
  isRun,
  onUpdate,
}: DesignElementProps<DesignBadge>) => {
  const strictLang = useStrictLang()
  // Списка может не быть вовсе (битые данные бека) — это тот же «пустой» случай.
  const items = badgeItems(element.badges)
  // Прохождение: плеер академии на пустом списке рисует `null` — пилюля-заглушка
  // ниже это авторский аффорданс формы, ученик её не увидит никогда.
  if (items.length === 0 && isRun) return null
  const badges = items.length > 0 ? items : [placeholderBadge()]
  const size = badgeSizeOf(badges.length)
  const style = badgeVariantStyle(resolveBadgeVariant(element.badgeVariant))

  return (
    // Бейджи всегда по центру и горизонтально (вертикальная раскладка убрана)
    <div className={BADGE_ROW_CLASS}>
      {badges.map((badge) =>
        // Правка на холсте: текст и иконка прямо в пилюле (кроме заглушки пустого списка).
        editable && !isRun && items.length > 0 ? (
          <EditableBadgePill
            key={badge.id}
            badge={badge}
            size={size}
            style={style}
            contentLang={contentLang}
            icon={
              badge.iconType !== 'none' && badge.iconValue ? (
                <BadgeIcon badge={badge} size={size} />
              ) : null
            }
            onPatch={(patch) =>
              onUpdate({ badges: items.map((b) => (b.id === badge.id ? { ...b, ...patch } : b)) })
            }
            onRemove={
              items.length > 1
                ? () => onUpdate({ badges: items.filter((b) => b.id !== badge.id) })
                : undefined
            }
          />
        ) : (
          <span
            key={badge.id}
            className={cn(
              'inline-flex items-center font-semibold',
              badgeSizeClass(size),
              'rounded-full',
            )}
            style={style}
          >
            <BadgeIcon badge={badge} size={size} />
            {/* Симметричный фолбэк только в read-only: в правке пустой перевод остаётся пустым. */}
            {getLocalizedDisplay(badge, 'text', contentLang, editable || strictLang)}
          </span>
        ),
      )}
    </div>
  )
}
